import { Card, Col, Row, Skeleton, Tabs } from 'antd';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useBreadcrumbContext } from '../../../../domain/utility/AppContextProvider/BreadcrumbContextProvider';
import IntlMessages from '../../../../domain/utility/IntlMessages';
import { popupConfirm } from '../../../../shared/components/popup-confirm';
import { productTagServices } from '../../../../shared/services/product-tag';
import { CreateProductTagDto } from '../../../../shared/services/product-tag/dtos/create-product-tag-dto';
import CreateProductTagHeader from './create-product-tag-header';
import { stockAdjustmentSchema } from './schema';
import type { TabsProps } from 'antd';
import ProductTagInfo from './product-tag-info';
import { ITagDataProducts } from '../../../../shared/services/product-tag/dtos/product-tag';
import { Form } from 'formik-antd';
import ProductTagProducts from './product-tag-products';
import { getLocaleValue } from 'src/shared/functions/change-locales';
import LanguageCode from 'src/shared/services/types/language-code';
import MainUtils from 'src/shared/utils/main';

interface ICreateProductTagProps { }

const CreateProductTag = (props: ICreateProductTagProps) => {
  const { setBreadcrumb }: any = useBreadcrumbContext();

  const { messages } = useIntl();

  const navigate = useNavigate();

  const { id } = useParams();

  const [initialValuesForm, setInitialValuesForm] =
    useState<CreateProductTagDto>();

  const [isProductTagDetailsLoading, setIsProductTagDetailsLoading] =
    useState(false);

  const [productTagDetails, setProductTagDetails] =
    useState<ITagDataProducts>();

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          setIsProductTagDetailsLoading(true);
          let productTagDetails = await productTagServices.getProductTagByID(
            id,
          );
          setProductTagDetails(productTagDetails);
        } catch (error) {
          console.error(error);
        } finally {
          setIsProductTagDetailsLoading(false);
        }
      })();
    }
  }, [id]);

  useEffect(() => {
    setBreadcrumb([
      {
        text: <IntlMessages id='Catalog.sideBarName' />,
        url: '/catalog/products',
      },
      {
        text: <IntlMessages id='catalog.productTags' />,
        url: '/catalog/product-tags',
      },
      id
        ? {
          text: <IntlMessages id='productTags.editProductTags' />,
          url: `/catalog/product-tags/edit-product-tag/${id}`,
        }
        : {
          text: <IntlMessages id='productTags.createProductTags' />,
          url: '/catalog/product-tags/create-product-tag',
        },
    ]);
  }, [id]);

  const itemsTab: TabsProps['items'] = !id
    ? [
      {
        key: 'productTagInfo',
        label: `${messages['productTags.productTagInfo'].toString()}`,
        children: <ProductTagInfo />,
      },
    ]
    : [
      {
        key: 'productTagInfo',
        label: `${messages['productTags.productTagInfo'].toString()}`,
        children: <ProductTagInfo />,
      },
      {
        key: 'products',
        label: `${messages['productTags.products'].toString()}`,
        children: <ProductTagProducts />,
      },
    ];

  useEffect(() => {
    if (productTagDetails)
      setInitialValuesForm({
        name: productTagDetails?.Name,
        description: productTagDetails?.Description,
        id: productTagDetails?.Id,
        productCount: productTagDetails?.ProductCount,
        seName: productTagDetails?.SeName,
        locales:
          productTagDetails?.Locales?.map((item) => {
            return {
              id: item.Id,
              languageId: item.LanguageId,
              localeKey: item.LocaleKey,
              localeValue: item.LocaleValue,
            };
          }) ?? [],
      });
  }, [productTagDetails]);

  const initialValues: CreateProductTagDto = {
    name: '',
    description: '',
  };

  const deleteProductTag = () => {
    popupConfirm(async () => {
      try {
        let checkDelete = await productTagServices.deleteProductTag(
          productTagDetails?.Id ?? '',
        );
        if (!Object.prototype.hasOwnProperty.call(checkDelete, 'error'))
          navigate('/catalog/product-tags');
      } catch (error) {
        console.error(error);
      }
    }, `${messages['commom.areYouSureYouWantToDelete'].toString()}` + ' ' + [productTagDetails?.Name] + `?`);
  };

  const handleSubmit = async (values: any, setSubmitting: any) => {
    try {
      let checkCreateOrEdit: any;
      if (!id)
        checkCreateOrEdit = await productTagServices.createProductTag({
          ...values,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
        });
      else {
        checkCreateOrEdit = await productTagServices.editProductTag({
          ...values,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
        });
      }
      if (!Object.prototype.hasOwnProperty.call(checkCreateOrEdit, 'error'))
        navigate('/catalog/product-tags');
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Formik
        enableReinitialize
        initialValues={initialValuesForm ? initialValuesForm : initialValues}
        validationSchema={stockAdjustmentSchema}
        onSubmit={(values, { setSubmitting }: any) =>
          handleSubmit(values, setSubmitting)
        }
        validateOnChange={false}
        validateOnBlur={false}>
        {({ handleSubmit, isSubmitting }: any) => (
          <Form>
            <Skeleton active loading={isProductTagDetailsLoading}>
              <Row gutter={[0, 48]}>
                <Col span={24}>
                  <CreateProductTagHeader
                    deleteProductTag={deleteProductTag}
                    productTagDetails={productTagDetails}
                    isSubmitting={isSubmitting}
                    handleSubmit={handleSubmit}
                  />
                </Col>

                <Col span={24}>
                  <Tabs defaultActiveKey='productTagInfo' items={itemsTab} />
                </Col>
              </Row>
            </Skeleton>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export default CreateProductTag;
